import { NetworkError, fetchPublicAPI } from "hooks/usePublicAPI";
import { postGrantRequest } from "pages/Login/login-grant-api";
import {
  grantFailureKind,
  parseGrantLookup,
} from "pages/Login/login-grant-flow";
import { apiErrorCode } from "utils/errors";

import type { Handoff } from "./handoff";
import {
  parseConfirmRefusal,
  parseConfirmedMint,
  parseConfirmedVia,
  parseMintedGrant,
  parseSubmissionStatus,
  type LookupAnswer,
  type MintAnswer,
  type SignInConfirmAnswer,
  type SignInConfirmClient,
  type SubmissionStatusClient,
  type StatusAnswer,
} from "./submissionStatus";

/** The signed-in visitor's token, or null when signed out or unreadable. */
export type IdTokenReader = () => Promise<string | null>;

async function status(
  submissionId: string,
  readIdToken: IdTokenReader,
): Promise<StatusAnswer> {
  try {
    const idToken = await readIdToken().catch(() => null);
    const raw = await fetchPublicAPI<unknown>(
      `/leads/submissions/${encodeURIComponent(submissionId)}/status`,
      idToken === null
        ? {}
        : { headers: { Authorization: `Bearer ${idToken}` } },
    );
    const parsed = parseSubmissionStatus(raw);
    return parsed ? { kind: "found", status: parsed } : { kind: "failed" };
  } catch (err) {
    if (err instanceof NetworkError && err.statusCode === 404) {
      return { kind: "missing" };
    }
    return { kind: "failed" };
  }
}

async function lookup(grant: string): Promise<LookupAnswer> {
  try {
    const raw = await postGrantRequest<unknown>("/auth/login-grants/lookup", {
      login_grant: grant,
    });
    const parsed = parseGrantLookup(raw);
    return parsed ? { kind: "valid", lookup: parsed } : { kind: "invalid" };
  } catch (err) {
    if (
      err instanceof NetworkError &&
      grantFailureKind(apiErrorCode(err), err.statusCode) === "grant_invalid"
    ) {
      return { kind: "invalid" };
    }
    return { kind: "failed" };
  }
}

async function mint(
  submissionId: string,
  readIdToken: IdTokenReader,
  handoff: Handoff | null,
): Promise<MintAnswer> {
  try {
    const idToken = await readIdToken().catch(() => null);
    const raw = await fetchPublicAPI<unknown>(
      `/leads/submissions/${encodeURIComponent(submissionId)}/grant`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(idToken === null ? {} : { Authorization: `Bearer ${idToken}` }),
        },
        body: JSON.stringify(handoff === null ? {} : { handoff }),
      },
    );
    const next = parseConfirmedMint(raw);
    if (next) return { kind: "confirmed", next, via: parseConfirmedVia(raw) };
    const parsed = parseMintedGrant(raw);
    return parsed
      ? { kind: "minted", grant: { ...parsed, unmasked: handoff !== null } }
      : { kind: "failed" };
  } catch (err) {
    if (err instanceof NetworkError && err.statusCode === 404) {
      return { kind: "missing" };
    }
    if (err instanceof NetworkError && err.statusCode === 429) {
      return { kind: "rate_limited" };
    }
    if (
      err instanceof NetworkError &&
      err.statusCode === 409 &&
      apiErrorCode(err) === "login_grant_unavailable"
    ) {
      return { kind: "unavailable" };
    }
    return { kind: "failed" };
  }
}

async function signInConfirm(
  submissionId: string,
  readIdToken: IdTokenReader,
): Promise<SignInConfirmAnswer> {
  try {
    const idToken = await readIdToken().catch(() => null);
    if (idToken === null) return { kind: "not_confirmed" };
    const raw = await fetchPublicAPI<unknown>(
      `/leads/submissions/${encodeURIComponent(submissionId)}/sign-in-confirm`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({}),
      },
    );
    const next = parseConfirmedMint(raw);
    if (next) return { kind: "confirmed", next };
    const reason = parseConfirmRefusal(raw);
    if (reason === "pending") return { kind: "pending" };
    return reason ? { kind: "refused", reason } : { kind: "not_confirmed" };
  } catch {
    return { kind: "not_confirmed" };
  }
}

/** The handoff to present for a submission, or null when there is none. */
export type HandoffReader = (submissionId: string) => Handoff | null;

/** The status route answers a quote, case and event id only to the Person
 * the submission is about, and the grant route confirms a submission on the
 * spot for that Person's own sign-in, so a signed-in visitor's token rides
 * along on both. The grant route also takes the signed link's handoff, which
 * earns the visitor their destinations as typed. */
export function submissionStatusClientFor(
  readIdToken: IdTokenReader,
  readHandoff: HandoffReader = () => null,
): SubmissionStatusClient & SignInConfirmClient {
  return {
    signInConfirm: (submissionId) => signInConfirm(submissionId, readIdToken),
    status: (submissionId) => status(submissionId, readIdToken),
    lookup,
    mint: (submissionId) =>
      mint(submissionId, readIdToken, readHandoff(submissionId)),
  };
}
